/** @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import { InputNumber, DatePicker, Select, Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import TimePickerWithInterval from 'src/components/ts-components/TimePickerWithInterval'
import {
  HARD_CONSTRAINT_TOOLTIP,
  SOFT_CONSTRAINT_TOOLTIP,
} from '../../constants'
import type { Venue } from '../../hooks/useSchedulerLocations'

const { Option } = Select

/**
 * Days of week options (0=Sunday, 6=Saturday)
 */
const DAYS_OF_WEEK = [
  { value: 0, short: 'Sun' },
  { value: 1, short: 'Mon' },
  { value: 2, short: 'Tue' },
  { value: 3, short: 'Wed' },
  { value: 4, short: 'Thu' },
  { value: 5, short: 'Fri' },
  { value: 6, short: 'Sat' },
]

// ============================================
// HardSoftToggle (controlled, no Form.Item)
// ============================================

export const HardSoftToggle = ({
  value,
  onChange,
  lockedHard = false,
}: {
  value?: boolean
  onChange?: (val: boolean) => void
  // When true, the constraint is always HARD (e.g. venue_capacity — a physical
  // limit). Render a single non-switchable HARD badge so the no-op SOFT choice
  // isn't offered; the backend enforces it hard regardless.
  lockedHard?: boolean
}) => {
  const isHard = lockedHard ? true : !!value
  if (lockedHard) {
    return (
      <div css={toggleContainer}>
        <Tooltip title='Venue capacity is a physical limit — always enforced as a hard constraint'>
          <button
            type='button'
            disabled
            css={css`
              ${toggleBtn(true, true)};
              cursor: default;
            `}
          >
            HARD
          </button>
        </Tooltip>
      </div>
    )
  }
  return (
    <div css={toggleContainer}>
      <Tooltip title={HARD_CONSTRAINT_TOOLTIP}>
        <button
          type='button'
          onClick={() => onChange?.(true)}
          css={toggleBtn(isHard, true)}
        >
          HARD
        </button>
      </Tooltip>
      <Tooltip title={SOFT_CONSTRAINT_TOOLTIP}>
        <button
          type='button'
          onClick={() => onChange?.(false)}
          css={toggleBtn(!isHard, false)}
        >
          SOFT
        </button>
      </Tooltip>
    </div>
  )
}

// ============================================
// DayPicker — circular toggle buttons
// ============================================

export const DayPicker = ({
  value,
  onChange,
}: {
  value?: number[]
  onChange?: (val: number[]) => void
}) => {
  const selected = value || []
  const toggle = (day: number) => {
    const next = selected.includes(day)
      ? selected.filter((d) => d !== day)
      : [...selected, day]
    onChange?.(next)
  }
  return (
    <div css={dayPickerRow}>
      {DAYS_OF_WEEK.map((d) => {
        const isActive = selected.includes(d.value)
        return (
          <button
            key={d.value}
            type='button'
            onClick={() => toggle(d.value)}
            css={dayBtn(isActive)}
          >
            {d.short}
          </button>
        )
      })}
    </div>
  )
}

// ============================================
// ConstraintParameterFields
// ============================================

export interface ConstraintParameterFieldsProps {
  typeCode: string
  values: Record<string, unknown>
  onChange: (field: string, value: unknown) => void
  venues?: Venue[]
  fullWidth?: boolean
}

const FIELD_LABELS: Record<string, Record<string, string>> = {
  min_rest_days: { min_days: 'Minimum Rest Days' },
  no_back_to_back: { min_minutes_between: 'Minimum Minutes Between Games' },
  max_games_per_team_per_day: { max_games: 'Max Games Per Day' },
  games_per_team_per_week: { games_per_week: 'Games Per Week' },
  preferred_day: { preferred_days: 'Preferred Days' },
  restricted_day: { restricted_days: 'Restricted Days' },
  max_consecutive_late_games: {
    late_time_threshold: 'Late Game Threshold',
    max_consecutive: 'Max Consecutive Late Games',
  },
  team_bye_period: { bye_date_range: 'Bye Period' },
  venue_blackout: {
    dates: 'Blackout Dates',
    venue_uids: 'Venues',
    facility_uids: 'Facilities',
  },
  venue_capacity: {
    facility_uids: 'Facilities',
    max_concurrent_games: 'Max Concurrent Games',
  },
  time_window: {
    start_time: 'Start Time',
    end_time: 'End Time',
    days: 'Days (optional)',
  },
  bye_distribution: { max_consecutive_byes: 'Max Consecutive Byes' },
  home_away_balance: { target_home_percentage: 'Target Home %' },
  preferred_time: { start_time: 'Start Time', end_time: 'End Time' },
}

const FieldWrapper: React.FC<{
  fullWidth?: boolean
  typeCode: string
  fieldKey: string
  children: React.ReactNode
}> = ({ fullWidth, typeCode, fieldKey, children }) => {
  if (!fullWidth) {
    return <>{children}</>
  }
  const label = FIELD_LABELS[typeCode]?.[fieldKey]
  return (
    <div css={fieldContainerStyles}>
      {label && <span css={fieldLabelStyles}>{label}</span>}
      {children}
    </div>
  )
}

const ConstraintParameterFields: React.FC<ConstraintParameterFieldsProps> = ({
  typeCode,
  values,
  onChange,
  venues = [],
  fullWidth,
}) => {
  const facilities = venues.flatMap((v) =>
    v.facilities.map((f) => ({ uid: f.id, name: `${v.name} — ${f.name}` }))
  )

  const inputCss = fullWidth ? inputFullWidth : inputCompact
  const selectCss = fullWidth ? selectFullWidth : selectCompact
  const dateStyle = fullWidth ? ({ width: '100%' } as const) : dateRangeCompact

  switch (typeCode) {
    case 'min_rest_days':
      return (
        <FieldWrapper
          fullWidth={fullWidth}
          typeCode={typeCode}
          fieldKey='min_days'
        >
          <InputNumber
            min={1}
            max={14}
            value={values.min_days as number}
            onChange={(v) => onChange('min_days', v)}
            css={inputCss}
          />
        </FieldWrapper>
      )

    case 'no_back_to_back':
      return (
        <FieldWrapper
          fullWidth={fullWidth}
          typeCode={typeCode}
          fieldKey='min_minutes_between'
        >
          <InputNumber
            min={15}
            max={10080}
            step={15}
            value={values.min_minutes_between as number}
            onChange={(v) => onChange('min_minutes_between', v)}
            addonAfter='min'
            css={inputCss}
          />
        </FieldWrapper>
      )

    case 'max_games_per_team_per_day':
      return (
        <FieldWrapper
          fullWidth={fullWidth}
          typeCode={typeCode}
          fieldKey='max_games'
        >
          <InputNumber
            min={1}
            max={10}
            value={values.max_games as number}
            onChange={(v) => onChange('max_games', v)}
            css={inputCss}
          />
        </FieldWrapper>
      )

    case 'games_per_team_per_week':
      return (
        <FieldWrapper
          fullWidth={fullWidth}
          typeCode={typeCode}
          fieldKey='games_per_week'
        >
          <InputNumber
            min={1}
            max={7}
            value={values.games_per_week as number}
            onChange={(v) => onChange('games_per_week', v)}
            css={inputCss}
          />
        </FieldWrapper>
      )

    case 'preferred_day':
      return (
        <FieldWrapper
          fullWidth={fullWidth}
          typeCode={typeCode}
          fieldKey='preferred_days'
        >
          <DayPicker
            value={values.preferred_days as number[]}
            onChange={(v) => onChange('preferred_days', v)}
          />
        </FieldWrapper>
      )

    case 'restricted_day':
      return (
        <FieldWrapper
          fullWidth={fullWidth}
          typeCode={typeCode}
          fieldKey='restricted_days'
        >
          <DayPicker
            value={values.restricted_days as number[]}
            onChange={(v) => onChange('restricted_days', v)}
          />
        </FieldWrapper>
      )

    case 'max_consecutive_late_games':
      return (
        <>
          <FieldWrapper
            fullWidth={fullWidth}
            typeCode={typeCode}
            fieldKey='late_time_threshold'
          >
            <TimePickerWithInterval
              value={values.late_time_threshold as any}
              onChange={(v) => onChange('late_time_threshold', v)}
              style={fullWidth ? { width: '100%' } : undefined} // eslint-disable-line react-native/no-inline-styles
            />
          </FieldWrapper>
          <FieldWrapper
            fullWidth={fullWidth}
            typeCode={typeCode}
            fieldKey='max_consecutive'
          >
            <InputNumber
              min={1}
              max={10}
              value={values.max_consecutive as number}
              onChange={(v) => onChange('max_consecutive', v)}
              css={inputCss}
            />
          </FieldWrapper>
        </>
      )

    case 'team_bye_period':
      return (
        <FieldWrapper
          fullWidth={fullWidth}
          typeCode={typeCode}
          fieldKey='bye_date_range'
        >
          <DatePicker.RangePicker
            style={dateStyle}
            format='YYYY-MM-DD'
            value={values.bye_date_range as [any, any]}
            onChange={(v) => onChange('bye_date_range', v)}
          />
        </FieldWrapper>
      )

    case 'venue_blackout':
      return (
        <>
          <FieldWrapper
            fullWidth={fullWidth}
            typeCode={typeCode}
            fieldKey='dates'
          >
            <DatePicker.RangePicker
              style={dateStyle}
              format='YYYY-MM-DD'
              value={values.dates as [any, any]}
              onChange={(v) => onChange('dates', v)}
            />
          </FieldWrapper>
          <FieldWrapper
            fullWidth={fullWidth}
            typeCode={typeCode}
            fieldKey='venue_uids'
          >
            <Select
              mode='multiple'
              placeholder='Venues'
              allowClear
              showSearch
              optionFilterProp='children'
              popupClassName='dark-select-popup'
              value={values.venue_uids as string[]}
              onChange={(v) => onChange('venue_uids', v)}
              css={selectCss}
            >
              {venues.map((v) => (
                <Option key={v.id} value={v.id}>
                  {v.name}
                </Option>
              ))}
            </Select>
          </FieldWrapper>
          <FieldWrapper
            fullWidth={fullWidth}
            typeCode={typeCode}
            fieldKey='facility_uids'
          >
            <Select
              mode='multiple'
              placeholder='Facilities'
              allowClear
              showSearch
              optionFilterProp='children'
              popupClassName='dark-select-popup'
              value={values.facility_uids as string[]}
              onChange={(v) => onChange('facility_uids', v)}
              css={selectCss}
            >
              {facilities.map((f) => (
                <Option key={f.uid} value={f.uid}>
                  {f.name}
                </Option>
              ))}
            </Select>
          </FieldWrapper>
        </>
      )

    case 'venue_capacity':
      return (
        <>
          <FieldWrapper
            fullWidth={fullWidth}
            typeCode={typeCode}
            fieldKey='facility_uids'
          >
            <Select
              mode='multiple'
              placeholder='Facilities'
              allowClear
              showSearch
              optionFilterProp='children'
              popupClassName='dark-select-popup'
              value={values.facility_uids as string[]}
              onChange={(v) => onChange('facility_uids', v)}
              css={selectCss}
            >
              {facilities.map((f) => (
                <Option key={f.uid} value={f.uid}>
                  {f.name}
                </Option>
              ))}
            </Select>
          </FieldWrapper>
          <FieldWrapper
            fullWidth={fullWidth}
            typeCode={typeCode}
            fieldKey='max_concurrent_games'
          >
            <InputNumber
              min={1}
              max={20}
              value={values.max_concurrent_games as number}
              onChange={(v) => onChange('max_concurrent_games', v)}
              css={inputCss}
            />
          </FieldWrapper>
        </>
      )

    case 'time_window':
      return (
        <>
          {fullWidth ? (
            <div css={timeRangeRowStyles}>
              <FieldWrapper
                fullWidth={fullWidth}
                typeCode={typeCode}
                fieldKey='start_time'
              >
                <TimePickerWithInterval
                  value={values.start_time as any}
                  onChange={(v) => onChange('start_time', v)}
                  style={{ width: '100%' }} // eslint-disable-line react-native/no-inline-styles
                />
              </FieldWrapper>
              <FieldWrapper
                fullWidth={fullWidth}
                typeCode={typeCode}
                fieldKey='end_time'
              >
                <TimePickerWithInterval
                  value={values.end_time as any}
                  onChange={(v) => onChange('end_time', v)}
                  style={{ width: '100%' }} // eslint-disable-line react-native/no-inline-styles
                  relativeToTime={values.start_time as any}
                />
              </FieldWrapper>
            </div>
          ) : (
            <>
              <TimePickerWithInterval
                value={values.start_time as any}
                onChange={(v) => onChange('start_time', v)}
              />
              <TimePickerWithInterval
                value={values.end_time as any}
                onChange={(v) => onChange('end_time', v)}
                relativeToTime={values.start_time as any}
              />
            </>
          )}
          <FieldWrapper
            fullWidth={fullWidth}
            typeCode={typeCode}
            fieldKey='days'
          >
            <DayPicker
              value={values.days as number[]}
              onChange={(v) => onChange('days', v)}
            />
          </FieldWrapper>
        </>
      )

    case 'bye_distribution':
      return (
        <FieldWrapper
          fullWidth={fullWidth}
          typeCode={typeCode}
          fieldKey='max_consecutive_byes'
        >
          <InputNumber
            min={1}
            max={10}
            value={values.max_consecutive_byes as number}
            onChange={(v) => onChange('max_consecutive_byes', v)}
            css={inputCss}
          />
        </FieldWrapper>
      )

    case 'home_away_balance':
      return (
        <FieldWrapper
          fullWidth={fullWidth}
          typeCode={typeCode}
          fieldKey='target_home_percentage'
        >
          <InputNumber
            min={0}
            max={100}
            value={values.target_home_percentage as number}
            onChange={(v) => onChange('target_home_percentage', v)}
            addonAfter='%'
            css={inputCss}
          />
        </FieldWrapper>
      )

    case 'preferred_time':
      return fullWidth ? (
        <div css={timeRangeRowStyles}>
          <FieldWrapper
            fullWidth={fullWidth}
            typeCode={typeCode}
            fieldKey='start_time'
          >
            <TimePickerWithInterval
              value={values.start_time as any}
              onChange={(v) => onChange('start_time', v)}
              style={{ width: '100%' }} // eslint-disable-line react-native/no-inline-styles
            />
          </FieldWrapper>
          <FieldWrapper
            fullWidth={fullWidth}
            typeCode={typeCode}
            fieldKey='end_time'
          >
            <TimePickerWithInterval
              value={values.end_time as any}
              onChange={(v) => onChange('end_time', v)}
              style={{ width: '100%' }} // eslint-disable-line react-native/no-inline-styles
              relativeToTime={values.start_time as any}
            />
          </FieldWrapper>
        </div>
      ) : (
        <>
          <TimePickerWithInterval
            value={values.start_time as any}
            onChange={(v) => onChange('start_time', v)}
          />
          <TimePickerWithInterval
            value={values.end_time as any}
            onChange={(v) => onChange('end_time', v)}
            relativeToTime={values.start_time as any}
          />
        </>
      )

    default:
      return null
  }
}

// ─── Styles ──────────────────────────────────────────

const toggleContainer = css`
  display: inline-flex;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
`

const toggleBtn = (active: boolean, isHard: boolean) => css`
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1.44px;
  border: none;
  cursor: pointer;
  color: ${colors.NEUTRAL_30};
  opacity: ${active ? 1 : 0.5};
  background: ${active
    ? isHard
      ? colors.ERROR_500
      : colors.INFO_500
    : colors.SECONDARY_400};
  transition: background 0.15s, opacity 0.15s;
  text-transform: uppercase;
`

const dayPickerRow = css`
  display: flex;
  gap: 8px;
`

const dayBtn = (isActive: boolean) => css`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: ${isActive ? 'none' : `1px solid ${colors.SECONDARY_100}`};
  background: ${isActive ? colors.PRIMARY : 'transparent'};
  color: ${isActive ? colors.SECONDARY_300 : colors.NEUTRAL_100};
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  padding: 0;

  &:hover {
    border-color: ${isActive ? 'none' : colors.NEUTRAL_100};
    background: ${isActive
      ? colors.PRIMARY_HOVER
      : `${colors.SECONDARY_100}80`};
  }
`

const inputCompact = css`
  width: 80px;
`

const selectCompact = css`
  min-width: 140px;
  max-width: 180px;
`

const dateRangeCompact = { width: 200 } as const

const inputFullWidth = css`
  width: 100%;
`

const selectFullWidth = css`
  width: 100%;
`

const fieldContainerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const timeRangeRowStyles = css`
  display: flex;
  align-items: flex-end;
  gap: 8px;

  & > div {
    flex: 1;
  }
`

const fieldLabelStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20.3px;
  color: ${colors.WHITE};
`

export default ConstraintParameterFields
